import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChipSelect } from '../../../../ChipsSelect/ChipSelect';
import { Skeleton } from '../../../../Skeleton/Skeleton';
import { SystemIconButton } from '../../../../Icon';
import { Typography } from '../../../../Typography';
import { useDebounce, useDeepCompare } from '../../../../../hooks';
import { useHover } from '../../../../../hooks/useHover';
import {
  OnQuickReportsChanged,
  QueryModel,
  SortModel
} from '../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickActionsContext } from '../../../utils/DataTableQuickActionsContext';
import { useDataTableQuickReportsContext } from '../../../utils/DataTableQuickReportsContext';
import { filterQuickReports } from '../utils/QuickReports.utils';
import { QuickReportOrigin } from '../utils/QuickReports.type';
import { useIntlUds } from '../../../../../translations/UdsIntlProvider';
import { PluginDestination } from '../../../plugins/impl';
import { useFetchingState } from '../utils/useFetchingState';
import { usePropsForwarderContext } from '../../../plugins/impl/context/ForwarderContext';

const SCROLL_AMOUNT = 300;

export interface QuickReportsExportPanelProps<Q extends QueryModel, S extends SortModel> {
  onQuickReportClick?: (id?: string, origin?: QuickReportOrigin) => void;
  /*! Deliver INITIAL quickReports (should be deliver on first table render) */
  quickReports: OnQuickReportsChanged<Q, S, unknown, string, string, string, string>[] | undefined;
  /*! Use onQuickReportsChanged to update quickReports on BE side.
   * You don't have to update local quickReports state base onQuickReportsChanged action
   */
  onQuickReportsChanged:
    | ((
        report: OnQuickReportsChanged<Q, S, unknown, string, string, string, string>[],
        shouldNotifyUserAboutChange: boolean
      ) => void)
    | undefined;
  loadingQuickReportsDone: boolean;
}

export const QuickReportsExportPanel = <Q extends QueryModel, S extends SortModel>(
  props: QuickReportsExportPanelProps<Q, S>
) => {
  const { setForwarderProps } = usePropsForwarderContext();

  const propsHasChanged = useDeepCompare({
    quickReports: props.quickReports,
    loadingQuickReportsDone: props.loadingQuickReportsDone
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (propsHasChanged) {
      setForwarderProps('quickReports', props);
    }
  }, [props, propsHasChanged]);

  return null;
};

QuickReportsExportPanel.id = 'QuickReportsExportPanel';

export const QuickReportsExportPanelInternalComponent = <Q extends QueryModel, S extends SortModel>() => {
  const { getProps } = usePropsForwarderContext();
  const { onQuickReportClick } = getProps('quickReports') ?? {};
  const { intl } = useIntlUds();
  const [showScrolls, setShowScrolls] = useState<{ right: boolean; left: boolean }>({ right: false, left: false });
  const [chipContainerRef, isChipContainerHovered] = useHover<HTMLDivElement>();
  const [rightBtnRef, isRightBtnHovered] = useHover<HTMLButtonElement>();
  const [leftBtnRef, isLeftBtnHovered] = useHover<HTMLButtonElement>();
  const { quickReports, activeQuickReportId, setQuickReportDrawer, setOnQuickReportClick } =
    useDataTableQuickReportsContext<Q, S>();
  const { selectQuickReport } = useDataTableQuickActionsContext();

  const pinnedReports = useMemo(() => filterQuickReports<Q, S>(true, quickReports), [quickReports]);
  const { isPageLoading, isDataLoading } = useFetchingState();

  const scrollButtonsVisibilityHandler = useDebounce(() => {
    const element = chipContainerRef.current;
    if (element) {
      // Arrows should be visible only on hover
      // Arrows shouldn't disappear when scrolls end user still hover an arrow.
      // Otherwise user can accidentally click chip actions.
      const elementIsHovered = isChipContainerHovered || isLeftBtnHovered || isRightBtnHovered;
      setShowScrolls({
        left: elementIsHovered && (element.scrollLeft !== 0 || isLeftBtnHovered),
        right:
          elementIsHovered && (element.scrollWidth - element.clientWidth !== element.scrollLeft || isRightBtnHovered)
      });
    }
  }, 50);

  const handleQuickReportClick = useCallback(
    (quickReportId: string, toggle: boolean) => {
      onQuickReportClick?.(quickReportId, 'quick-report-panel');
      selectQuickReport(quickReportId, toggle);
    },
    [onQuickReportClick, selectQuickReport]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    setOnQuickReportClick(() => onQuickReportClick);
  }, []);

  // scrollButtonsVisibilityHandler should be ignored for performance reasons
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    scrollButtonsVisibilityHandler();
  }, [pinnedReports, isChipContainerHovered, isLeftBtnHovered, isRightBtnHovered]);

  const scrollLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const element = chipContainerRef.current;
      if (element) {
        element.scrollLeft += -SCROLL_AMOUNT;
        scrollButtonsVisibilityHandler();
      }
    },
    [chipContainerRef, scrollButtonsVisibilityHandler]
  );

  const scrollRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const element = chipContainerRef.current;
      if (element) {
        element.scrollLeft += SCROLL_AMOUNT;
        scrollButtonsVisibilityHandler();
      }
    },
    [chipContainerRef, scrollButtonsVisibilityHandler]
  );

  const hasPinnedReports = quickReports?.some((config) => config.pinned);
  return (
    <StyledQuickReportsWrapper data-test="quick-reports-top-panel">
      <StyledQuickViewTitle variant="label1">{intl.DataTable.QuickViews.quickView}</StyledQuickViewTitle>
      <SystemIconButton
        disabled={isPageLoading || isDataLoading}
        onClick={() => setQuickReportDrawer(true)}
        iconName="tune"
        dataTest="quick-report-button"
      />
      <StyledChipsSection>
        <StyledSystemIconButtonWrapper $left $show={showScrolls.left}>
          <SystemIconButton iconName="arrow_left_alt" onClick={scrollLeft} size="small" ref={leftBtnRef} />
        </StyledSystemIconButtonWrapper>
        <StyledChipsWrapper ref={chipContainerRef}>
          {isPageLoading ? (
            <>
              <Skeleton
                randomWidthAsNumber={[80, 120]}
                height={32}
                inline
                variant="rect"
                style={{ borderRadius: 16 }}
              />
              <Skeleton
                randomWidthAsNumber={[80, 120]}
                height={32}
                inline
                variant="rect"
                style={{ borderRadius: 16 }}
              />
            </>
          ) : hasPinnedReports ? (
            pinnedReports.map((report) => {
              const isActive = activeQuickReportId === report.quickReportId;
              return (
                <StyledChipSelect
                  dataTest="quick-report-chip"
                  key={report.quickReportId}
                  disabled={isPageLoading}
                  onClick={() => handleQuickReportClick(report.quickReportId, true)}
                  selected={isActive}
                  label={
                    <>
                      <Typography variant="label2" noWrap>
                        {report.name}
                      </Typography>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <PluginDestination name="quickViewsPanelChipMenu" contextValue={report} />
                      </span>
                    </>
                  }
                />
              );
            })
          ) : (
            !!quickReports && (
              <Typography variant="body1">{intl.DataTable.QuickViews.Panel.noQuickViewsMessage}</Typography>
            )
          )}
        </StyledChipsWrapper>
        <StyledSystemIconButtonWrapper $left={false} $show={showScrolls.right}>
          <SystemIconButton iconName="arrow_right_alt" onClick={scrollRight} size="small" ref={rightBtnRef} />
        </StyledSystemIconButtonWrapper>
      </StyledChipsSection>
    </StyledQuickReportsWrapper>
  );
};

const StyledQuickViewTitle = styled(Typography)`
  white-space: nowrap;
`;

const StyledChipsSection = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledSystemIconButtonWrapper = styled.div<{ $left: boolean; $show: boolean }>`
  position: absolute;
  left: ${({ $left }) => ($left ? 0 : 'inherit')};
  right: ${({ $left }) => ($left ? 'inherit' : '0')};
  display: ${({ $show }) => ($show ? 'inherit' : 'none')};
  top: -5px;
  background: white;
  border-radius: 50%;
  z-index: 100;
`;

const StyledChipsWrapper = styled.div`
  position: relative;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacingSize.m};
  align-items: center;
  display: flex;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
`;

const StyledChipSelect = styled(ChipSelect)`
  position: relative;
  height: 32px;
  padding-right: 0;

  .text {
    margin-left: ${({ theme }) => theme.spacingSize.m};
    > span {
      gap: 0;
    }
  }
  .label-text {
    display: flex;
    align-items: center;
    padding-right: 0;

    & > span {
      padding-left: 8px;
    }
  }
`;

const StyledQuickReportsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  margin: ${({ theme }) => theme.spacingSize.xl};
  align-items: center;
  height: 32px;
  overflow: hidden;
`;
